import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import BottomNav from '../components/BottomNav';
import { Button } from '../components/ui/button';
import { ArrowLeft, Check, X, FileText, Award, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';

export default function WorksheetOfDay() {
  const { token, API } = useAuth();
  const navigate = useNavigate();
  const [worksheet, setWorksheet] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const fetchWotd = async () => {
      try {
        const res = await axios.get(`${API}/student/worksheet-of-day`, { headers, withCredentials: true });
        setWorksheet(res.data.worksheet);
        setQuestions(res.data.questions);
        if (res.data.worksheet?.completed) setSubmitted(true);
      } catch (e) { toast.error('Could not load worksheet'); }
      finally { setLoading(false); }
    };
    if (token) fetchWotd();
  }, [API, token]);

  const q = questions[currentIdx];
  const isAnswered = q && answers[q.id] !== undefined;

  const handleAnswer = (val) => {
    setAnswers(prev => ({ ...prev, [q.id]: val }));
  };

  const handleSubmit = async () => {
    try {
      const res = await axios.post(`${API}/student/worksheet-of-day/submit`, {
        worksheet_id: worksheet.id, answers
      }, { headers, withCredentials: true });
      setResult(res.data);
      setSubmitted(true);
      toast.success(`Scored ${res.data.score}%! Earned ${res.data.coins_earned} coins`);
    } catch (e) { toast.error('Submission failed'); }
  };

  const renderMath = (text) => {
    if (!text) return null;
    try {
      const parts = text.split(/(\$\$[\s\S]*?\$\$|\$[^$]+?\$)/g);
      return parts.map((p, i) => {
        if (p.startsWith('$$') && p.endsWith('$$')) return <BlockMath key={i} math={p.slice(2, -2)} />;
        if (p.startsWith('$') && p.endsWith('$')) return <InlineMath key={i} math={p.slice(1, -1)} />;
        return <span key={i}>{p}</span>;
      });
    } catch { return text; }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#FFF8F0' }}>
      <div className="animate-spin w-8 h-8 border-4 border-[#8338EC] border-t-transparent rounded-full" />
    </div>
  );

  if (submitted && result) return (
    <div className="min-h-screen pb-safe" style={{ background: '#FFF8F0' }}>
      <div className="px-4 pt-4 pb-2 flex items-center gap-3">
        <button onClick={() => navigate('/home')}><ArrowLeft className="w-5 h-5 text-[#1B4F8A]" /></button>
        <h1 className="text-lg font-extrabold" style={{ color: '#1B4F8A' }}>Worksheet Complete!</h1>
      </div>
      <div className="px-4 py-8 text-center space-y-4" data-testid="wotd-result">
        <Award size={64} className="mx-auto text-[#FFD23F]" />
        <p className="text-4xl font-black" style={{ color: result.score >= 70 ? '#06D6A0' : '#E63946' }}>{result.score}%</p>
        <p className="text-lg font-bold" style={{ color: '#1B4F8A' }}>{result.correct}/{result.total} correct</p>
        <div className="bg-[#FFD23F]/20 border-2 border-[#FFD23F] rounded-2xl p-3">
          <p className="text-sm font-bold" style={{ color: '#1B4F8A' }}>+{result.coins_earned} MathCoins earned!</p>
        </div>
        <Button data-testid="wotd-back-btn" onClick={() => navigate('/home')} className="bg-[#FF6B35] text-white font-bold rounded-full px-6">Back to Home</Button>
      </div>
      <BottomNav active="home" />
    </div>
  );

  if (submitted) return (
    <div className="min-h-screen pb-safe flex items-center justify-center" style={{ background: '#FFF8F0' }}>
      <div className="text-center space-y-4">
        <Check size={48} className="mx-auto text-[#06D6A0]" />
        <p className="text-lg font-bold" style={{ color: '#1B4F8A' }}>Already completed today!</p>
        <Button onClick={() => navigate('/home')} className="bg-[#FF6B35] text-white font-bold rounded-full px-6">Back Home</Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen pb-safe" style={{ background: '#FFF8F0' }}>
      <div className="px-4 pt-4 pb-2 flex items-center gap-3">
        <button onClick={() => navigate('/home')}><ArrowLeft className="w-5 h-5 text-[#1B4F8A]" /></button>
        <div className="flex-1">
          <h1 className="text-base font-extrabold" style={{ color: '#1B4F8A' }}>Daily Worksheet</h1>
          <p className="text-xs" style={{ color: '#8B7355' }}>{questions.length} questions · Personalized</p>
        </div>
        <span className="text-xs font-bold bg-[#8338EC] text-white px-2 py-1 rounded-full">
          {currentIdx + 1}/{questions.length}
        </span>
      </div>

      {/* Progress bar */}
      <div className="px-4 mb-3">
        <div className="h-2 bg-gray-200 rounded-full">
          <div className="h-2 bg-[#8338EC] rounded-full transition-all" style={{ width: `${((Object.keys(answers).length) / questions.length) * 100}%` }} />
        </div>
      </div>

      {q && (
        <div className="px-4 space-y-4" data-testid="wotd-question">
          {/* Question card */}
          <div className="bg-white border-2 rounded-3xl p-5" style={{ borderColor: '#E5DACE', boxShadow: '0 4px 0 #E5DACE' }}>
            <p className="text-xs font-semibold mb-2" style={{ color: '#8B7355' }}>
              {q.question_type?.replace(/_/g, ' ').toUpperCase()} · {q.marks || 1}m
            </p>
            <p className="text-sm font-bold" style={{ color: '#1B4F8A' }}>
              {renderMath(q.question_latex || q.question_text)}
            </p>
          </div>

          {/* Answer options */}
          {q.question_type === 'mcq' && ['A', 'B', 'C', 'D'].map(opt => {
            const val = q[`option_${opt.toLowerCase()}`];
            if (!val) return null;
            const isSelected = answers[q.id] === opt;
            return (
              <button key={opt} data-testid={`wotd-option-${opt}`}
                onClick={() => handleAnswer(opt)}
                className="w-full text-left bg-white border-2 rounded-2xl p-3 flex items-center gap-3 transition-all"
                style={{
                  borderColor: isSelected ? '#FF6B35' : '#E5DACE',
                  background: isSelected ? '#FFF0E5' : 'white',
                }}>
                <span className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2"
                  style={{ borderColor: isSelected ? '#FF6B35' : '#E5DACE', color: isSelected ? '#FF6B35' : '#8B7355' }}>
                  {opt}
                </span>
                <span className="text-sm font-semibold" style={{ color: '#1B4F8A' }}>{renderMath(val)}</span>
              </button>
            );
          })}

          {q.question_type === 'true_false' && ['true', 'false'].map(opt => (
            <button key={opt} data-testid={`wotd-tf-${opt}`}
              onClick={() => handleAnswer(opt)}
              className="w-full text-left bg-white border-2 rounded-2xl p-3 font-semibold text-sm transition-all"
              style={{
                borderColor: answers[q.id] === opt ? '#FF6B35' : '#E5DACE',
                background: answers[q.id] === opt ? '#FFF0E5' : 'white',
                color: '#1B4F8A'
              }}>
              {opt === 'true' ? 'True' : 'False'}
            </button>
          ))}

          {['fill_blank', 'integer_type', 'subjective'].includes(q.question_type) && (
            <input data-testid="wotd-text-input"
              type="text" placeholder="Type your answer..."
              value={answers[q.id] || ''}
              onChange={e => handleAnswer(e.target.value)}
              className="w-full border-2 rounded-2xl p-3 text-sm font-semibold"
              style={{ borderColor: '#E5DACE', color: '#1B4F8A' }}
            />
          )}

          {/* Navigation */}
          <div className="flex gap-2 pt-2 pb-4">
            {currentIdx > 0 && (
              <Button onClick={() => setCurrentIdx(i => i - 1)} variant="outline" className="flex-1 rounded-full font-bold">Previous</Button>
            )}
            {currentIdx < questions.length - 1 ? (
              <Button data-testid="wotd-next-btn" onClick={() => setCurrentIdx(i => i + 1)}
                className="flex-1 bg-[#FF6B35] text-white rounded-full font-bold" disabled={!isAnswered}>
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button data-testid="wotd-submit-btn" onClick={handleSubmit}
                className="flex-1 bg-[#06D6A0] text-white rounded-full font-bold"
                disabled={Object.keys(answers).length < questions.length}>
                Submit Worksheet
              </Button>
            )}
          </div>
        </div>
      )}
      <BottomNav active="home" />
    </div>
  );
}
